import React, { useState } from 'react';
import { Upload, Download, AlertCircle, CheckCircle, X, FileSpreadsheet, Package, AlertTriangle, Info } from 'lucide-react';

const ImportPage = () => {
  const [activeTab, setActiveTab] = useState('services');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Import Data</h1>
          <p className="text-gray-600 mt-2">Bulk import services and products from Excel spreadsheets</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('services')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'services'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileSpreadsheet size={20} />
              Import Services
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'products'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Package size={20} />
              Import Products
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'services' ? <ServiceImport /> : <ProductImport />}
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/calculator/import/services', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setFile(null);
        document.getElementById('service-file-input').value = '';
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Error uploading file: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch('/api/calculator/import/services/template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'service_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error downloading template: ' + err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Import Instructions</h3>
            <ul className="text-sm text-blue-800 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column A:</strong> Category (optional, inherits from previous row)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column B:</strong> Service Name (required)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column C:</strong> Description (optional)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column D:</strong> Duration in minutes (optional, defaults to 60)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column E:</strong> Price (optional)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column F:</strong> Staff Name (optional, must match existing staff)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Template Download */}
      <div>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 rounded-lg transition-all font-medium"
        >
          <Download size={20} />
          Download Template
        </button>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Select Excel File
        </label>
        <div className="flex items-center gap-4">
          <label className="flex-1 cursor-pointer">
            <input
              id="service-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              file 
                ? 'border-green-400 bg-green-50' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}>
              <Upload className={`mx-auto mb-2 ${file ? 'text-green-600' : 'text-gray-400'}`} size={32} />
              <p className="text-sm font-medium text-gray-700">
                {file ? file.name : 'Click to select or drag file here'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Excel files only (.xlsx, .xls)</p>
            </div>
          </label>
          {file && (
            <button
              onClick={() => {
                setFile(null);
                document.getElementById('service-file-input').value = '';
              }}
              className="text-gray-500 hover:text-red-600 transition-colors"
              title="Remove file"
            >
              <X size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Import Button */}
      <button
        onClick={handleImport}
        disabled={!file || importing}
        className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold text-lg transition-all ${
          !file || importing
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
        }`}
      >
        <Upload size={24} />
        {importing ? 'Importing Services...' : 'Import Services'}
      </button>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-semibold text-red-900">Error</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={24} />
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 text-lg">Import Completed Successfully!</h4>
              <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                <div className="bg-white rounded p-3">
                  <p className="text-green-600 font-semibold">✓ {result.imported} services imported</p>
                </div>
                {result.updated > 0 && (
                  <div className="bg-white rounded p-3">
                    <p className="text-blue-600 font-semibold">↻ {result.updated} services updated</p>
                  </div>
                )}
                {result.skipped > 0 && (
                  <div className="bg-white rounded p-3">
                    <p className="text-amber-600 font-semibold">⚠ {result.skipped} services skipped</p>
                  </div>
                )}
                {result.categories_created?.length > 0 && (
                  <div className="bg-white rounded p-3">
                    <p className="text-purple-600 font-semibold">+ {result.categories_created.length} categories created</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Categories Created */}
          {result.categories_created && result.categories_created.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <h5 className="font-semibold text-green-900 mb-2">Categories Created:</h5>
              <div className="flex flex-wrap gap-2">
                {result.categories_created.map((cat, idx) => (
                  <span key={idx} className="bg-white text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {result.warnings && result.warnings.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <h5 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <AlertTriangle size={16} />
                Warnings ({result.warnings.length})
              </h5>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-sm bg-white rounded">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold">Row</th>
                      <th className="text-left py-2 px-3 font-semibold">Service</th>
                      <th className="text-left py-2 px-3 font-semibold">Warning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.warnings.map((warn, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="py-2 px-3">{warn.row}</td>
                        <td className="py-2 px-3 font-medium">{warn.service}</td>
                        <td className="py-2 px-3 text-amber-700">{warn.warning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Errors */}
          {result.errors && result.errors.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <h5 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                <AlertCircle size={16} />
                Errors ({result.errors.length})
              </h5>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-sm bg-white rounded">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold">Row</th>
                      <th className="text-left py-2 px-3 font-semibold">Service</th>
                      <th className="text-left py-2 px-3 font-semibold">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="py-2 px-3">{err.row}</td>
                        <td className="py-2 px-3 font-medium">{err.service}</td>
                        <td className="py-2 px-3 text-red-700">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProductImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/calculator/import/products', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setFile(null);
        document.getElementById('product-file-input').value = '';
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Error uploading file: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch('/api/calculator/import/products/template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error downloading template: ' + err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Import Instructions</h3>
            <ul className="text-sm text-blue-800 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column A:</strong> Product Name (required)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column B:</strong> Pack Size (required) - e.g., 1000, 250, 5</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column C:</strong> Unit Type (optional) - ml, g, items, uses, etc.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column D:</strong> Cost Price in £ (required)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column E:</strong> Retail Price in £ (optional)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column F:</strong> Barcode (optional)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Column G:</strong> Reorder Level (optional)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Template Download */}
      <div>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 rounded-lg transition-all font-medium"
        >
          <Download size={20} />
          Download Template
        </button>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Select Excel File
        </label>
        <div className="flex items-center gap-4">
          <label className="flex-1 cursor-pointer">
            <input
              id="product-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              file 
                ? 'border-green-400 bg-green-50' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}>
              <Upload className={`mx-auto mb-2 ${file ? 'text-green-600' : 'text-gray-400'}`} size={32} />
              <p className="text-sm font-medium text-gray-700">
                {file ? file.name : 'Click to select or drag file here'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Excel files only (.xlsx, .xls)</p>
            </div>
          </label>
          {file && (
            <button
              onClick={() => {
                setFile(null);
                document.getElementById('product-file-input').value = '';
              }}
              className="text-gray-500 hover:text-red-600 transition-colors"
              title="Remove file"
            >
              <X size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Import Button */}
      <button
        onClick={handleImport}
        disabled={!file || importing}
        className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold text-lg transition-all ${
          !file || importing
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
        }`}
      >
        <Package size={24} />
        {importing ? 'Importing Products...' : 'Import Products'}
      </button>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-semibold text-red-900">Error</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={24} />
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 text-lg">Import Completed Successfully!</h4>
              <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                <div className="bg-white rounded p-3">
                  <p className="text-green-600 font-semibold">✓ {result.imported} products imported</p>
                </div>
                {result.updated > 0 && (
                  <div className="bg-white rounded p-3">
                    <p className="text-blue-600 font-semibold">↻ {result.updated} products updated</p>
                  </div>
                )}
                {result.skipped > 0 && (
                  <div className="bg-white rounded p-3">
                    <p className="text-amber-600 font-semibold">⚠ {result.skipped} products skipped</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Warnings */}
          {result.warnings && result.warnings.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <h5 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <AlertTriangle size={16} />
                Warnings ({result.warnings.length})
              </h5>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-sm bg-white rounded">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold">Row</th>
                      <th className="text-left py-2 px-3 font-semibold">Product</th>
                      <th className="text-left py-2 px-3 font-semibold">Warning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.warnings.map((warn, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="py-2 px-3">{warn.row}</td>
                        <td className="py-2 px-3 font-medium">{warn.product}</td>
                        <td className="py-2 px-3 text-amber-700">{warn.warning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Errors */}
          {result.errors && result.errors.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <h5 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                <AlertCircle size={16} />
                Errors ({result.errors.length})
              </h5>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-sm bg-white rounded">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold">Row</th>
                      <th className="text-left py-2 px-3 font-semibold">Product</th>
                      <th className="text-left py-2 px-3 font-semibold">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="py-2 px-3">{err.row}</td>
                        <td className="py-2 px-3 font-medium">{err.product}</td>
                        <td className="py-2 px-3 text-red-700">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportPage;